import { Typography, Box, Snackbar, Alert, Button, Radio, Tooltip, IconButton } from '@mui/material';
import { NoContent, ReactTable } from '../../../components/shared';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useState, useEffect, useRef } from 'react';
import useStyles from '../MachineDetails/style';
import { getDateFormat, getTimeFormat } from '../../../utils/common-methods';
import { ASSET_STATUS } from '../../../constants/assetStatus';
import { useNavigate } from 'react-router-dom';
import { routeConstants } from '../../../constants/routeConstants';
import {
  useGetMachineDetailsHistoryByIdQuery,
  useDeleteMachineHistoryMutation,
} from '../../../services/Machines/MachinesService';
import { Delete } from '@mui/icons-material';
import ConfirmationModal from '../../../components/ConfirmationModel';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 30;

function formatChanges(changes) {
  if (!changes) return '';
  if (Array.isArray(changes) && changes.length > 0 && typeof changes[0] === 'object') {
    return changes
      .filter((change) => !['subLocationId', 'inEffectFrom', 'inEffectTo', 'oldSublocationId'].includes(change.field))
      .map((change) => {
        if (change.field === 'statusId') {
          const prevStatus = STATUS_ID_TO_NAME[change.from] || change.from || 'N/A';
          const currStatus = STATUS_ID_TO_NAME[change.to] || change.to || 'N/A';
          return `Status : ${prevStatus} --> ${currStatus}`;
        }
        return `${change.field} : ${change.from ?? 'N/A'} --> ${change.to ?? 'N/A'}`;
      })
      .join('\n');
  }
  if (Array.isArray(changes)) {
    return changes.join('\n');
  }
  return '';
}

const STATUS_ID_TO_NAME = Object.entries(ASSET_STATUS).reduce((acc, [key, value]) => {
  acc[value] = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return acc;
}, {});

export default function MachineHistoryModal({ assetId }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [maxInEffectFrom, setMaxInEffectFrom] = useState();
  const skipInitialEffect = useRef(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [deleteMachineHistory, { isLoading: isDeleting }] = useDeleteMachineHistoryMutation();

  const {
    currentData: latestData,
    isError,
    isSuccess,
    isFetching,
    isLoading,
    refetch,
  } = useGetMachineDetailsHistoryByIdQuery(
    {
      id: assetId,
      page: currentPage,
      perPage: perPageNumber,
    },
    {
      skip: !assetId,
      refetchOnMountOrArgChange: true,
    },
  );

  const handleAddClick = () => {
    const selectedRow = tableData[selectedRowIndex];
    if (selectedRow) {
      navigate(`/${routeConstants.MACHINES_ROUTE}/${routeConstants.EDIT_MACHINE_ROUTE}/${selectedRow.assetId}`, {
        state: {
          version: selectedRow.version,
          createMachineHistory: true,
          maxInEffectFrom: maxInEffectFrom,
        },
      });
    }
  };

  const handleEditClick = () => {
    const selectedRow = tableData[selectedRowIndex];
    if (selectedRow) {
      navigate(`/${routeConstants.MACHINES_ROUTE}/${routeConstants.EDIT_MACHINE_ROUTE}/${selectedRow.assetId}`, {
        state: {
          version: selectedRow.version,
          disableInEffectFrom: true,
        },
      });
    }
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteMachineHistory({ id: rowToDelete.assetId, version: rowToDelete.version }).unwrap();
      showSnackbar('History record deleted successfully.', 'success');
      refetch();
    } catch (err) {
      console.error('Failed to delete machine history:', err);
      showSnackbar(err.data?.message || 'Failed to delete history record.', 'error');
    } finally {
      handleConfirmClose();
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleRadioClick = (row, index) => {
    setSelectedRowIndex(index);
  };

  useEffect(() => {
    if (isSuccess && latestData?.data?.assetHistory && Array.isArray(latestData.data.assetHistory)) {
      const mappedData = latestData.data.assetHistory.map((history) => ({
        assetId: history.assetId,
        version: history.version,
        machine: history.legacyAssetId ? `${history.title}: ${history.legacyAssetId}` : history.title || 'N/A',
        indicator: history.indicator,
        inEffectFrom: getDateFormat(history.inEffectFrom) || 'N/A',
        inEffectTo: getDateFormat(history.inEffectTo) || '',
        originalInEffectTo: history.inEffectTo,
        sublocation: history.sublocation?.name || 'N/A',
        changes: formatChanges(history.changes),
        status: STATUS_ID_TO_NAME[history.statusId] || history.statusId || 'N/A',
        updatedBy: history.user ? `${history.user.firstName} ${history.user.lastName}` : 'N/A',
        updatedAt: history.updatedAt
          ? `${getDateFormat(history.updatedAt)} ${getTimeFormat(history.updatedAt)}`
          : 'N/A',
      }));
      setTableData(mappedData);
      setTotalCount(latestData.pagination?.total || mappedData.length);

      if (mappedData.length > 0) {
        setSelectedRowIndex(0);
      }

      if (latestData.data.maxInEffectFrom) {
        setMaxInEffectFrom(latestData.data.maxInEffectFrom);
      }
    } else if (isError) {
      setTableData([]);
      setTotalCount(0);
      setSelectedRowIndex(null);
    }
  }, [isSuccess, isError, latestData]);

  useEffect(() => {
    if (skipInitialEffect.current) {
      skipInitialEffect.current = false;
      return;
    }
    if (assetId) {
      refetch();
    }
  }, [currentPage, refetch, assetId]);

  const pageChangeHandler = (newPage) => {
    setCurrentPage(newPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    setPerPageNumber(newPerPage);
  };

  const getRowErrorClass = (rowData) => (rowData?.original?.indicator === 'active' ? classes.activeRow : '');

  const columns = [
    {
      Header: '',
      accessor: 'radio',
      width: 50,
      disableSortBy: true,
      Cell: ({ row }) => (
        <Radio
          checked={selectedRowIndex === row.index}
          onChange={() => handleRadioClick(row.original, row.index)}
          name="machine-history-radio"
          color="primary"
        />
      ),
    },
    { Header: 'Machine', accessor: 'machine', width: 'auto' },
    { Header: 'In Effect From', accessor: 'inEffectFrom', width: 'auto' },
    { Header: 'In Effect To', accessor: 'inEffectTo', width: 'auto' },
    { Header: 'Sublocation', accessor: 'sublocation', width: 'auto' },
    {
      Header: 'Changes',
      accessor: 'changes',
      width: '400',
      Cell: ({ value }) => <span style={{ whiteSpace: 'pre-line' }}>{value}</span>,
    },
    { Header: 'Status', accessor: 'status', width: 'auto' },
    { Header: 'Updated By', accessor: 'updatedBy', width: 'auto' },
    {
      Header: 'Updated At',
      accessor: 'updatedAt',
      width: 'auto',
      Cell: ({ value }) => <span style={{ textTransform: 'none' }}>{value}</span>,
    },
    {
      Header: '',
      accessor: 'delete',
      width: 50,
      Cell: ({ row }) =>
        row.original.originalInEffectTo && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDeleteClick(row.original)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box display="flex" mb={2} justifyContent="space-between" alignItems="center">
        <Typography variant="h2"></Typography>
        {tableData.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" color="primary" onClick={handleEditClick} disabled={selectedRowIndex === null}>
              Edit
            </Button>

            <Button variant="contained" color="primary" onClick={handleAddClick} disabled={selectedRowIndex === null}>
              Add
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
        {tableData.length === 0 ? (
          <NoContent title="No Results Found" desc={<span>No results found.</span>} />
        ) : (
          <>
            {totalCount <= perPageNumber && (
              <style>
                {`
            .MuiTablePagination-root {
              display: none !important;
            }
          `}
              </style>
            )}
            <ReactTable
              data={tableData}
              currentPage={currentPage - 1}
              perPage={perPageNumber}
              totalCount={totalCount}
              columns={columns}
              onPageChange={pageChangeHandler}
              pagination={true}
              manualPagination={true}
              changeRowsPerPageOptions={true}
              onPerPageChange={perPageChangeHandler}
              rowClassName={getRowErrorClass}
            />
          </>
        )}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmationModal
        isOpen={confirmOpen}
        title="Confirm Deletion"
        msg={`Are you sure you want to delete the history record from ${rowToDelete?.inEffectFrom || 'N/A'} to ${
          rowToDelete?.inEffectTo || 'present'
        }? This action cannot be undone.`}
        buttons={[
          { text: isDeleting ? 'Deleting...' : 'Delete', value: 'delete', color: 'error', disabled: isDeleting },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          if (val === 'delete') {
            handleConfirmDelete();
          } else {
            handleConfirmClose();
          }
        }}
      />
    </Box>
  );
}
